import { CalendarDays, Download, FileBarChart2, Layers, RefreshCw, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { apiTelecharger } from '@/api/client';
import { cheminExportPilotage, useActionsPrevisionnel, usePilotage, useSourcePrevisionnel, useVersionPtbaApprouvee } from '@/api/hooks';
import type { ComparaisonRapport, DonneesExecution, FiltresPilotage, LigneExecution, RapportImportPrevisionnel, TypePeriodeRapport } from '@/api/types';
import { useSession } from '@/auth/session';
import { BlocRessources, ChaineDepense, constatsDesLignes, DetailLigne, ExecutionParMasse, IndicateursCles, PointsAttention, PrevuPayeParMois, TableAlertes, TableRubriques, TableServicesLignes, TrajectoireCumulee } from '@/components/budget';
import { BoutonExcel, Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, dateFr, dateHeureFr, nombre } from '@/lib/utils';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const PROFILS_IMPORT = ['DIRECTEUR_FINANCIER', 'CONTROLE_GESTION', 'ADMINISTRATEUR'];
const PROFILS_VERSIONS = [...PROFILS_IMPORT, 'DIRECTEUR_GENERAL', 'DGA_FINANCES', 'DGA_OPERATIONS'];
type Onglet = 'synthese' | 'detail' | 'alertes';

/** Barre des filtres : exercice, période (cumul, trimestre, mois), comparaison. */
function Filtres({ f, onChange }: { f: FiltresPilotage; onChange: (patch: Partial<FiltresPilotage>) => void }) {
  const an = new Date().getFullYear();
  const exercices = [an - 2, an - 1, an, an + 1];
  const segment = (v: TypePeriodeRapport, l: string) => (
    <button key={v} type="button" onClick={() => onChange({ type: v })} className={cn('rounded-md px-3 py-1.5 text-sm transition-colors', f.type === v ? 'bg-foreground text-background' : 'hover:bg-accent')} aria-pressed={f.type === v}>
      {l}
    </button>
  );
  return (
    <div className="flex flex-wrap items-end gap-x-5 gap-y-3 rounded-lg border bg-card px-4 py-3">
      <div className="space-y-1">
        <Label htmlFor="exercice" className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Exercice
        </Label>
        <SelectNatif id="exercice" value={f.exercice} onChange={(e) => onChange({ exercice: Number(e.target.value) })} className="h-9 w-28">
          {exercices.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </SelectNatif>
      </div>
      <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Période</div>
        <div className="flex items-center gap-1 rounded-md border p-0.5">
          {segment('CUMUL', 'Cumul annuel')}
          {segment('TRIMESTRE', 'Trimestre')}
          {segment('MOIS', 'Mois')}
        </div>
      </div>
      {f.type === 'MOIS' && (
        <div className="space-y-1">
          <Label htmlFor="mois" className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Mois
          </Label>
          <SelectNatif id="mois" value={f.mois ?? 1} onChange={(e) => onChange({ mois: Number(e.target.value) })} className="h-9 w-36">
            {MOIS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </SelectNatif>
        </div>
      )}
      {f.type === 'TRIMESTRE' && (
        <div className="space-y-1">
          <Label htmlFor="trimestre" className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Trimestre
          </Label>
          <SelectNatif id="trimestre" value={f.trimestre ?? 1} onChange={(e) => onChange({ trimestre: Number(e.target.value) })} className="h-9 w-40">
            {[1, 2, 3, 4].map((t) => (
              <option key={t} value={t}>
                {t}
                {t === 1 ? 'er' : 'e'} trimestre
              </option>
            ))}
          </SelectNatif>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="comparaison" className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Comparer à
        </Label>
        <SelectNatif id="comparaison" value={f.comparaison} onChange={(e) => onChange({ comparaison: e.target.value as ComparaisonRapport })} className="h-9 w-56">
          <option value="AUCUNE">Aucune comparaison</option>
          <option value="T-1">Période précédente</option>
          <option value="N-1">Même période {f.exercice - 1}</option>
        </SelectNatif>
      </div>
    </div>
  );
}

/** Import du budget prévisionnel : fichier Excel ou CSV (export Google Sheets) ou lien du classeur ; lien de référence mémorisé et synchronisation. */
function DialogueImport({ ouvert, onOpenChange, exercice: initial }: { ouvert: boolean; onOpenChange: (o: boolean) => void; exercice: number }) {
  const actions = useActionsPrevisionnel();
  const [exercice, setExercice] = useState(initial);
  const source = useSourcePrevisionnel(exercice, ouvert);
  const [mode, setMode] = useState<'fichier' | 'lien'>('fichier');
  const [fichier, setFichier] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [memoriser, setMemoriser] = useState(true);
  const [rapport, setRapport] = useState<RapportImportPrevisionnel | null>(null);
  const lienEnregistre = source.data?.url ?? null;
  useEffect(() => {
    if (lienEnregistre && !url) {
      setUrl(lienEnregistre);
      setMode('lien');
    }
  }, [lienEnregistre, url]);
  const enCours = actions.importerFichier.isPending || actions.importerLien.isPending || actions.synchroniser.isPending || actions.enregistrerSource.isPending;
  const pret = mode === 'fichier' ? Boolean(fichier) : /^https:\/\/\S+$/.test(url.trim());
  const peutEcrire = Boolean(rapport && rapport.simulation && !rapport.erreurs.length && rapport.exercice === exercice);

  const annoncer = (r: RapportImportPrevisionnel) => {
    setRapport(r);
    if (r.ecrit) toast.success(`Budget prévisionnel ${exercice} mis à jour : ${r.lignes.creees} ligne(s) créée(s), ${r.lignes.modifiees} modifiée(s).`);
    else if (r.erreurs.length) toast.error(`${r.erreurs.length} erreur(s) : rien n'a été écrit.`);
    else toast.info('Simulation terminée, aucune écriture : vérifiez le compte rendu puis importez.');
  };
  const lancer = async (simulation: boolean) => {
    try {
      if (mode === 'lien' && memoriser && url.trim() !== lienEnregistre) await actions.enregistrerSource.mutateAsync({ exercice, url: url.trim(), automatique: source.data?.automatique ?? false });
      const r = mode === 'fichier' ? await actions.importerFichier.mutateAsync({ exercice, fichier: fichier!, simulation }) : mode === 'lien' && memoriser ? await actions.synchroniser.mutateAsync({ exercice, simulation }) : await actions.importerLien.mutateAsync({ exercice, url: url.trim(), simulation });
      annoncer(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import impossible.');
    }
  };
  const synchroniser = async (simulation: boolean) => {
    try {
      annoncer(await actions.synchroniser.mutateAsync({ exercice, simulation }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Synchronisation impossible.');
    }
  };
  const basculerAutomatique = async (automatique: boolean) => {
    if (!lienEnregistre) return;
    try {
      await actions.enregistrerSource.mutateAsync({ exercice, url: lienEnregistre, automatique });
      toast.success(automatique ? 'Synchronisation automatique chaque nuit activée.' : 'Synchronisation automatique désactivée.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const retirerLien = async () => {
    try {
      await actions.enregistrerSource.mutateAsync({ exercice, url: null, automatique: false });
      setUrl('');
      toast.success('Lien de référence retiré.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retrait impossible.');
    }
  };
  const modele = async () => {
    try {
      await apiTelecharger(`/budget/previsionnel/modele.xlsx?exercice=${exercice}`, `budget-previsionnel-${exercice}.xlsx`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Téléchargement impossible.');
    }
  };
  const anomalies = (liste: RapportImportPrevisionnel['erreurs'], classe: string) => (
    <ul className={cn('max-h-40 space-y-0.5 overflow-y-auto rounded-md border p-2 text-xs', classe)}>
      {liste.slice(0, 40).map((a, k) => (
        <li key={k}>
          {a.ligne ? `Ligne ${a.ligne}` : 'Fichier'}
          {a.code ? ` · ${a.code}` : ''} : {a.message}
        </li>
      ))}
      {liste.length > 40 && <li>… et {liste.length - 40} autre(s).</li>}
    </ul>
  );
  const s = source.data;
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Budget prévisionnel : import et synchronisation</DialogTitle>
          <DialogDescription>Le fichier de budget prévisionnel tenu dans Google Sheets est la source des lignes de l'exercice : contrôle complet, puis écriture tout ou rien. Les virements déjà passés sont conservés ; les lignes absentes du fichier ne sont pas supprimées.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {s?.url && (
            <div className="space-y-2 rounded-md border border-sky-200 bg-sky-50/60 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">Classeur de référence de l'exercice {s.exercice}</div>
                  <a href={s.url} target="_blank" rel="noreferrer" className="block truncate text-xs text-primary hover:underline" title={s.url}>
                    {s.url}
                  </a>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {s.derniereSynchronisation ? (
                      <>
                        Dernière synchronisation {dateHeureFr(s.derniereSynchronisation)} ({s.dernierResume?.origine}) :{' '}
                        <span className={s.dernierEtat === 'OK' ? 'font-medium text-emerald-700' : 'font-medium text-red-700'}>{s.dernierEtat === 'OK' ? 'réussie' : 'en échec'}</span>
                        {s.dernierResume?.lignes ? ` · ${s.dernierResume.lignes.creees} créée(s), ${s.dernierResume.lignes.modifiees} modifiée(s), ${s.dernierResume.lignes.inchangees} inchangée(s)` : ''}
                        {s.dernierResume?.message ? ` · ${s.dernierResume.message}` : ''}
                      </>
                    ) : (
                      'Jamais synchronisé'
                    )}
                    {s.modifieParNom ? ` · lien enregistré par ${s.modifieParNom}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" disabled={enCours} onClick={() => synchroniser(true)}>
                    Simuler
                  </Button>
                  <Button type="button" size="sm" disabled={enCours} onClick={() => synchroniser(false)}>
                    <RefreshCw className={cn('size-4', actions.synchroniser.isPending && 'animate-spin')} /> Synchroniser maintenant
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox checked={s.automatique} disabled={enCours} onCheckedChange={(c) => basculerAutomatique(c === true)} />
                  Synchroniser automatiquement chaque nuit (05 h 30) ; un échec est journalisé et visible ici
                </label>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" disabled={enCours} onClick={retirerLien}>
                  Retirer le lien
                </Button>
              </div>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="imp-exercice">Exercice</Label>
              <Input id="imp-exercice" type="number" min={2020} max={2100} value={exercice} onChange={(e) => (setExercice(Number(e.target.value)), setRapport(null), setUrl(''))} />
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={mode === 'fichier' ? 'default' : 'outline'} onClick={() => (setMode('fichier'), setRapport(null))}>
                  Fichier Excel ou CSV
                </Button>
                <Button type="button" size="sm" variant={mode === 'lien' ? 'default' : 'outline'} onClick={() => (setMode('lien'), setRapport(null))}>
                  Lien Google Sheets
                </Button>
              </div>
            </div>
          </div>
          {mode === 'fichier' ? (
            <div className="space-y-1.5">
              <Label htmlFor="imp-fichier">Fichier (.xlsx ou .csv exporté de Google Sheets)</Label>
              <Input id="imp-fichier" type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={(e) => (setFichier(e.target.files?.[0] ?? null), setRapport(null))} />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="imp-url">Lien du classeur Google Sheets</Label>
              <Input id="imp-url" type="url" placeholder="https://docs.google.com/spreadsheets/d/…/edit" value={url} onChange={(e) => (setUrl(e.target.value), setRapport(null))} />
              <label className="flex items-center gap-2 text-xs">
                <Checkbox checked={memoriser} onCheckedChange={(c) => setMemoriser(c === true)} />
                Mémoriser ce lien comme classeur de référence de l'exercice (synchronisations ultérieures)
              </label>
              <p className="text-xs text-muted-foreground">Dans Google Sheets : Partager › « Toute personne disposant du lien » (lecteur), puis coller le lien de la barre d'adresse. L'onglet « Budget prévisionnel » est lu s'il existe, sinon le premier onglet qui porte l'en-tête attendu.</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={modele}>
              <Download className="size-3.5" /> Télécharger le modèle (lignes actuelles de l'exercice, codes des directions et des services)
            </Button>
          </div>
          {rapport && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={rapport.ecrit ? 'default' : rapport.erreurs.length ? 'destructive' : 'secondary'}>{rapport.ecrit ? 'Importé' : rapport.erreurs.length ? 'Refusé' : 'Simulation'}</Badge>
                <span className="truncate text-muted-foreground">{rapport.origine}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                <div>
                  <div className="text-xs text-muted-foreground">Lignes lues</div>
                  <div className="num font-medium">{rapport.lignes.lues}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Créées · modifiées · inchangées</div>
                  <div className="num font-medium">
                    {rapport.lignes.creees} · {rapport.lignes.modifiees} · {rapport.lignes.inchangees}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Budget du fichier</div>
                  <div className="num font-medium">{nombre(rapport.totaux.budgetPrevisionnel)} USD</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Plans mensuels</div>
                  <div className="num font-medium">
                    {rapport.plans.enregistres} enregistré(s){rapport.plans.ignores ? ` · ${rapport.plans.ignores} ignoré(s)` : ''}
                  </div>
                </div>
              </div>
              {rapport.erreurs.length > 0 && anomalies(rapport.erreurs, 'border-red-200 bg-red-50 text-red-800')}
              {rapport.avertissements.length > 0 && anomalies(rapport.avertissements, 'border-amber-200 bg-amber-50 text-amber-800')}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant="outline" disabled={!pret || enCours} onClick={() => lancer(true)}>
            {enCours ? 'Contrôle…' : 'Simuler (sans écrire)'}
          </Button>
          <Button disabled={!peutEcrire || enCours} onClick={() => lancer(false)} title={peutEcrire ? undefined : 'Lancez d’abord une simulation sans erreur'}>
            <Upload className="size-4" /> Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Suivi budgétaire (maquette « Pilotage budgétaire ») : synthèse du Fonds, détail par direction, alertes ; période, comparaison, export. */
export function Budget() {
  const session = useSession();
  const u = session!.utilisateur;
  const peutImporter = u.profils.some((p) => PROFILS_IMPORT.includes(p));
  const [params, setParams] = useSearchParams();
  const maintenant = new Date();
  const [filtres, setFiltres] = useState<FiltresPilotage>({
    exercice: Number(params.get('exercice')) || maintenant.getFullYear(),
    type: (['CUMUL', 'MOIS', 'TRIMESTRE'].includes(params.get('periode') ?? '') ? params.get('periode') : 'CUMUL') as TypePeriodeRapport,
    mois: maintenant.getMonth() + 1,
    trimestre: Math.ceil((maintenant.getMonth() + 1) / 3),
    comparaison: 'AUCUNE',
  });
  const [direction, setDirection] = useState<string>(params.get('structure') ?? '');
  const [onglet, setOnglet] = useState<Onglet>(params.get('structure') ? 'detail' : 'synthese');
  const [ligne, setLigne] = useState<{ id: string; code: string; libelle: string } | null>(null);
  const [importOuvert, setImportOuvert] = useState(false);
  const source = useSourcePrevisionnel(filtres.exercice, peutImporter);
  const peutVoirVersions = u.profils.some((p) => PROFILS_VERSIONS.includes(p));
  const ptba = useVersionPtbaApprouvee(filtres.exercice);

  // Synthèse : périmètre par défaut (tout le Fonds si autorisé) ; détail : direction ou service choisi (« DIR » ou « DIR/SVC »).
  const requete = useMemo<FiltresPilotage>(() => {
    const [structureCode, serviceCode] = onglet === 'detail' && direction ? direction.split('/') : [undefined, undefined];
    return { ...filtres, structureCode, serviceCode };
  }, [filtres, onglet, direction]);
  const { data, isLoading, error, isFetching } = usePilotage(requete);
  const fonds = data?.perimetres.fonds ?? false;

  useEffect(() => {
    if (data && !direction) {
      const premiere = data.perimetres.structures[0]?.code ?? data.perimetres.services[0]?.perimetre ?? '';
      if (premiere) setDirection(premiere);
    }
  }, [data, direction]);
  useEffect(() => {
    const p = new URLSearchParams(params);
    if (onglet === 'detail' && direction) p.set('structure', direction);
    else p.delete('structure');
    if (filtres.type !== 'CUMUL') p.set('periode', filtres.type);
    else p.delete('periode');
    if (filtres.exercice !== maintenant.getFullYear()) p.set('exercice', String(filtres.exercice));
    else p.delete('exercice');
    if (p.toString() !== params.toString()) setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onglet, direction, filtres.type, filtres.exercice]);

  if (isLoading && !data) return <Chargement />;
  if ((error && !data) || !data) return <ErreurBloc erreur={error} />;
  const d: DonneesExecution = data;
  const vueDirection = !fonds || onglet === 'detail';
  const nombreAlertes = constatsDesLignes(d).filter((x) => x.constat.niveau === 'ALERTE').length;
  const ouvrirLigne = (l: LigneExecution) => setLigne({ id: l.id, code: l.code, libelle: l.libelle });
  const majFiltres = (patch: Partial<FiltresPilotage>) => setFiltres((f) => ({ ...f, ...patch }));

  return (
    <div className="space-y-5">
      <PageHeader
        titre={`Suivi budgétaire ${d.exercice}`}
        sousTitre={
          <span>
            {d.perimetre.libelle} · {d.periode.libelle} · données au {dateFr(d.dateSituation)} · montants en USD
            {source.data?.url && (
              <span className="ml-1">
                · budget prévisionnel {source.data.derniereSynchronisation ? `synchronisé le ${dateHeureFr(source.data.derniereSynchronisation)}` : 'non encore synchronisé'} avec le classeur Google Sheets
                {source.data.dernierEtat === 'ERREUR' && <span className="ml-1 font-medium text-red-700">(dernière synchronisation en échec)</span>}
              </span>
            )}
            {ptba.data?.version && (
              <span className="ml-1">
                · PTBA {ptba.data.version.numero} approuvé le {dateFr(ptba.data.version.dateDecision)}
              </span>
            )}
            {ptba.data && ptba.data.provisoires > 0 && <span className="ml-1 font-medium text-amber-700">· {ptba.data.provisoires} version(s) provisoire(s) du PTBA en attente (hors disponible)</span>}
            {isFetching && <span className="ml-2 text-xs">(actualisation…)</span>}
          </span>
        }
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/budget/plan-mensuel">
                <CalendarDays className="size-4" /> Plan mensuel
              </Link>
            </Button>
            {peutVoirVersions && (
              <Button variant="outline" asChild>
                <Link to={`/budget/versions?exercice=${filtres.exercice}`}>
                  <Layers className="size-4" /> Versions du PTBA
                </Link>
              </Button>
            )}
            {peutImporter && (
              <Button variant="outline" onClick={() => setImportOuvert(true)}>
                {source.data?.url ? <RefreshCw className="size-4" /> : <Upload className="size-4" />} {source.data?.url ? 'Synchroniser le budget prévisionnel' : 'Importer le budget prévisionnel'}
              </Button>
            )}
            <BoutonExcel chemin={cheminExportPilotage(requete)} nom={`suivi-budgetaire-${d.perimetre.code.replace('/', '-')}-${d.periode.slug}.xlsx`} taille="default" />
            <Button variant="outline" asChild>
              <Link to="/rapports">
                <FileBarChart2 className="size-4" /> Rapports d'exécution
              </Link>
            </Button>
          </>
        }
      />

      <Filtres f={filtres} onChange={majFiltres} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as Onglet)}>
          <TabsList>
            <TabsTrigger value="synthese">{fonds ? 'Synthèse du Fonds' : 'Synthèse'}</TabsTrigger>
            {fonds && <TabsTrigger value="detail">Détail par direction</TabsTrigger>}
            <TabsTrigger value="alertes">
              Alertes
              {nombreAlertes > 0 && <Badge variant="destructive" className="ml-1.5 px-1.5">{nombreAlertes}</Badge>}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {fonds && onglet === 'detail' && (
          <div className="flex items-center gap-2">
            <Label htmlFor="direction" className="text-sm text-muted-foreground">
              Direction
            </Label>
            <SelectNatif id="direction" value={direction} onChange={(e) => setDirection(e.target.value)} className="h-9 w-72">
              {d.perimetres.structures.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
              {d.perimetres.services.map((s) => (
                <option key={s.perimetre} value={s.perimetre}>
                  {s.perimetre} · {s.libelle} (service)
                </option>
              ))}
            </SelectNatif>
          </div>
        )}
      </div>

      {onglet === 'alertes' ? (
        <TableAlertes d={d} />
      ) : (
        <>
          <IndicateursCles d={d} nombreAlertes={nombreAlertes} />

          <div className="grid gap-5 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{vueDirection ? 'Prévu et payé par mois' : `Trajectoire cumulée ${d.exercice}`}</CardTitle>
                <CardDescription>{vueDirection ? 'Période sélectionnée surlignée · payé en rouge au-delà de 110 % du prévu, en vert sous 70 %.' : 'Le payé doit suivre la courbe du prévu : un écart qui se creuse signale un retard ou une accélération.'}</CardDescription>
              </CardHeader>
              <CardContent>{vueDirection ? <PrevuPayeParMois d={d} /> : <TrajectoireCumulee d={d} />}</CardContent>
            </Card>
            <div className="space-y-5">
              {!vueDirection && (
                <Card>
                  <CardHeader>
                    <CardTitle>Exécution par grande masse</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ExecutionParMasse d={d} />
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardHeader>
                  <CardTitle>Chaîne de la dépense</CardTitle>
                  <CardDescription>Formulaires d'engagement comptabilisés, puis payés par la trésorerie.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChaineDepense d={d} />
                </CardContent>
              </Card>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{vueDirection ? (d.services ? 'Services et lignes budgétaires' : 'Lignes budgétaires') : 'Exécution par rubrique'}</CardTitle>
              <CardDescription>{vueDirection ? 'Cliquez sur une ligne pour ses mouvements.' : 'Grandes masses puis directions ; cliquez sur une direction pour ouvrir son détail.'}</CardDescription>
            </CardHeader>
            <CardContent>{vueDirection ? <TableServicesLignes d={d} onLigne={ouvrirLigne} /> : <TableRubriques d={d} onStructure={(code) => (setDirection(code), setOnglet('detail'))} />}</CardContent>
          </Card>

          {!vueDirection && <PointsAttention d={d} onToutVoir={() => setOnglet('alertes')} />}

          {d.commentaires.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Lecture automatique</CardTitle>
                <CardDescription>Phrases établies à partir des chiffres, reprises dans les rapports d'exécution.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-disc space-y-1 pl-5 text-sm">
                  {d.commentaires.map((c, k) => (
                    <li key={k}>{c}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {d.ressources && !vueDirection && <BlocRessources r={d.ressources} />}
        </>
      )}

      <DetailLigne ligne={ligne} onClose={() => setLigne(null)} />
      {peutImporter && <DialogueImport ouvert={importOuvert} onOpenChange={setImportOuvert} exercice={filtres.exercice} />}
    </div>
  );
}
